// FILE: src/components/app/bases/BudgetBaseComponent/BudgetBaseComponent.js
import React from "react";
import PropTypes from "prop-types";
import styles from "./BudgetBaseComponent.module.css";

import BudgetAddForm from "./BudgetAddForm/BudgetAddForm";
import BudgetCard from "./BudgetCard/BudgetCard";

import useBudgets from "./hooks/useBudgets";
import usePlannedTransactions from "./hooks/usePlannedTransactions";
import useBudgetSummary from "./hooks/useBudgetSummary";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default function BudgetBaseComponent({ user }) {
  const [authUser, setAuthUser] = React.useState(() => {
    try { return getAuth()?.currentUser || null; } catch { return null; }
  });

  React.useEffect(() => {
    let unsub;
    try { unsub = onAuthStateChanged(getAuth(), (u) => setAuthUser(u)); } catch {}
    return () => { try { unsub && unsub(); } catch {} };
  }, []);

  const effectiveUser = user || authUser || null;
  const userEmail = String(effectiveUser?.email || "").toLowerCase();

  const {
    items: budgets,
    itemsEnriched,
    accounts,
    createBudget,
    updateBudget,
    deleteBudget,
    addPlannedTx,
    deletePlannedTx,
  } = useBudgets({ user: effectiveUser });

  const { netWorthTx, monthlyFreeCashFlow } = useBudgetSummary({ userEmail });

  const [expandedId, setExpandedId] = React.useState(null);

  const { getPlannedFor, refreshPlanned } = usePlannedTransactions({
    userEmail,
    horizonDays: 365,
  });

  const onCreated = async (payload) => {
    const id = await createBudget(payload);
    setExpandedId(id);
  };

  const handleAddPlannedTx = async (budget, tx) => {
    await addPlannedTx(budget, tx);
    refreshPlanned();
  };

  const handleDeletePlannedTx = async (txId) => {
    await deletePlannedTx(txId);
    refreshPlanned();
  };

  const handleDeleteBudget = async (budgetId) => {
    await deleteBudget(budgetId); // cascades planned-tx delete
    refreshPlanned();             // ensure UI reflects removal immediately
    setExpandedId((cur) => (cur === budgetId ? null : cur));
  };

  const findBudgetById = React.useCallback(
    (id) => budgets.find((b) => b.id === id),
    [budgets]
  );

  return (
    <div className={styles.wrap}>
      <div className="container-fluid">
        <div className="pp-page">
          <div className="pp-page-header d-flex align-items-center justify-content-between">
            <h1 className="pp-page-title mb-0">Budgets</h1>
            <div className="pp-page-actions" />
          </div>

          <div className="pp-section">
            {/* Summary cards */}
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Estimated Net Worth (from transactions)</div>
                    <div className="fs-4">
                      {Number(netWorthTx).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Monthly Free Cash Flow (last ~90 days)</div>
                    <div className="fs-4">
                      {Number(monthlyFreeCashFlow).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Budget Items</div>
                    <div className="fs-4">{itemsEnriched.length}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Add Budget */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Plan a future expense</h2>
                <BudgetAddForm
                  user={effectiveUser}
                  mode="create"
                  accounts={accounts}
                  onSubmit={onCreated}
                  onCancel={() => {}}
                />
              </div>
            </div>

            {/* Items */}
            {itemsEnriched.length === 0 ? (
              <div className="text-muted">No planned budgets yet. Add one above.</div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {itemsEnriched.map((it) => (
                  <BudgetCard
                    key={it.id}
                    item={it}
                    plannedTransactions={getPlannedFor(it)}
                    accounts={accounts}
                    expanded={expandedId === it.id}
                    onToggle={() => setExpandedId((cur) => (cur === it.id ? null : it.id))}
                    onDelete={() => handleDeleteBudget(it.id)}
                    onAddPlannedTx={(id, tx) => {
                      const b = findBudgetById(id);
                      if (b) handleAddPlannedTx(b, tx);
                    }}
                    onDeletePlannedTx={(_, txId) => handleDeletePlannedTx(txId)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

BudgetBaseComponent.propTypes = {
  user: PropTypes.object,
};
